import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Avatar,
  Chip,
  CircularProgress,
  Alert,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableRow,
  LinearProgress,
  FormControlLabel,
  Switch,
  IconButton,
  Tooltip
} from '@mui/material';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import api from '../api/axiosConfig';
import { getMediaBaseUrl } from '../utils/mediaUrl';
import { usePositions } from '../utils/positions';

const BOOST_OPTIONS = [
  { key: 'altura', labelKey: 'measurements.labels.altura' },
  { key: 'envergadura', labelKey: 'measurements.labels.envergadura' },
  { key: 'peso', labelKey: 'measurements.labels.peso' },
  { key: 'alcance', labelKey: 'measurements.labels.alcance' },
  { key: 'salto_vertical_maximo', labelKey: 'measurements.labels.saltoVerticalMaximo' },
  { key: 'salto_vertical_de_pie', labelKey: 'measurements.labels.saltoVerticalDePie' },
  { key: 'agilidad', labelKey: 'measurements.labels.agilidad' },
  { key: 'sprint_cuarto', labelKey: 'measurements.labels.sprintCuarto' },
  { key: 'grasa_corporal', labelKey: 'measurements.labels.grasaCorporal' },
  { key: 'longitud_mano', labelKey: 'measurements.labels.longitudMano' },
  { key: 'ancho_mano', labelKey: 'measurements.labels.anchoMano' }
];

const SimilarPlayersTab = ({ playerId }) => {
  const { t } = useTranslation();
  const { translateFromDB } = usePositions(t);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filterPosition, setFilterPosition] = useState(true);
  const [includeNba, setIncludeNba] = useState(true);
  const [boostStats, setBoostStats] = useState([]);

  const toggleBoost = (key) => {
    setBoostStats(prev =>
      prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
    );
  };

  useEffect(() => {
    if (!playerId) return;
    setLoading(true);
    setError(null);

    const params = {
      playerId,
      filterPosition: filterPosition.toString(),
      includeNba: includeNba.toString()
    };
    if (boostStats.length > 0) {
      params.boostStats = boostStats.join(',');
    }

    api.get('/api/medidas/similar-players', { params })
      .then(res => setData(res.data))
      .catch(() => setError(t('measurements.similar.error')))
      .finally(() => setLoading(false));
  }, [playerId, filterPosition, includeNba, boostStats, t]);

  const boostChips = (
    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
      <Typography variant="body2" color="text.secondary" sx={{ mr: 0.5, alignSelf: 'center', fontWeight: 500 }}>
        {t('measurements.similar.boost')}
      </Typography>
      {BOOST_OPTIONS.map(opt => (
        <Chip
          key={opt.key}
          label={t(opt.labelKey)}
          size="small"
          variant={boostStats.includes(opt.key) ? 'filled' : 'outlined'}
          color={boostStats.includes(opt.key) ? 'primary' : 'default'}
          onClick={() => toggleBoost(opt.key)}
          sx={{ fontSize: '0.7rem', height: 24, cursor: 'pointer' }}
        />
      ))}
    </Box>
  );

  if (loading) {
    return (
      <Box sx={{ p: { xs: 1, md: 2 } }}>
        {boostChips}
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: { xs: 1, md: 2 } }}>
        {boostChips}
        <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>
      </Box>
    );
  }

  if (!data || !data.similarPlayers || data.similarPlayers.length === 0) {
    return (
      <Box>
        <Box sx={{ p: { xs: 1, md: 2 }, mb: 1, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <FormControlLabel
              control={
                <Switch
                  checked={filterPosition}
                  onChange={(e) => setFilterPosition(e.target.checked)}
                  size="small"
                />
              }
              label={<Typography variant="body2">{t('measurements.similar.samePositionOnly')}</Typography>}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={includeNba}
                  onChange={(e) => setIncludeNba(e.target.checked)}
                  size="small"
                  color="warning"
                />
              }
              label={<Typography variant="body2">{t('measurements.similar.includeNba')}</Typography>}
            />
          </Box>
          {boostChips}
        </Box>
        <Card sx={{ p: 4, textAlign: 'center', bgcolor: 'grey.50', m: 2 }}>
          <Typography variant="h6" color="text.secondary">
            {t('measurements.similar.noResults')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {t('measurements.similar.noResultsHint')}
          </Typography>
        </Card>
      </Box>
    );
  }

  const getSimilarityColor = (pct) => {
    if (pct >= 85) return '#4CAF50';
    if (pct >= 70) return '#8BC34A';
    if (pct >= 55) return '#FF9800';
    return '#F44336';
  };

  const getDiffColor = (diff) => {
    if (diff > 0) return 'success.main';
    if (diff < 0) return 'error.main';
    return 'text.secondary';
  };

  const formatDiff = (diff, unit) => {
    const sign = diff > 0 ? '+' : '';
    return `${sign}${diff} ${unit}`;
  };

  return (
    <Box sx={{ p: { xs: 1, md: 2 } }}>
      <Box sx={{ mb: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Chip
              label={`${data.similarPlayers.length} ${t('measurements.similar.playersFound')}`}
              size="small"
              color="primary"
              variant="outlined"
            />
            <Tooltip
              title={t('measurements.similar.howItWorks')}
              arrow
              placement="bottom-start"
              slotProps={{ tooltip: { sx: { maxWidth: 350, fontSize: '0.8rem', lineHeight: 1.5 } } }}
            >
              <IconButton size="small" sx={{ p: 0.5 }}>
                <HelpOutlineIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
              </IconButton>
            </Tooltip>
          </Box>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <FormControlLabel
              control={
                <Switch
                  checked={filterPosition}
                  onChange={(e) => setFilterPosition(e.target.checked)}
                  size="small"
                />
              }
              label={
                <Typography variant="body2">
                  {t('measurements.similar.samePositionOnly')}
                </Typography>
              }
            />
            <FormControlLabel
              control={
                <Switch
                  checked={includeNba}
                  onChange={(e) => setIncludeNba(e.target.checked)}
                  size="small"
                  color="warning"
                />
              }
              label={
                <Typography variant="body2">
                  {t('measurements.similar.includeNba')}
                </Typography>
              }
            />
          </Box>
        </Box>
        {boostChips}
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {data.similarPlayers.map((player) => {
          const simColor = getSimilarityColor(player.similarity);
          const avatarSrc = player.isNba
            ? player.photoUrl
            : `${getMediaBaseUrl()}/photos/${player.playerId}.png?${Date.now()}`;

          return (
            <Card
              key={player.playerId}
              sx={{
                overflow: 'visible',
                border: '1px solid',
                borderColor: player.isNba ? 'warning.main' : 'divider',
                '&:hover': { boxShadow: 4 },
                transition: 'box-shadow 0.2s'
              }}
            >
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                {/* Header */}
                <Box sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 1.5
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Avatar
                      src={avatarSrc}
                      alt={player.playerName}
                      sx={{ width: 44, height: 44 }}
                    >
                      {player.playerName?.split(' ').map(n => n[0]).join('').slice(0, 2)}
                    </Avatar>
                    <Box>
                      {player.isNba ? (
                        <Typography
                          variant="subtitle1"
                          sx={{ fontWeight: 600 }}
                        >
                          {player.playerName}
                        </Typography>
                      ) : (
                        <Link
                          to={`/players/${player.playerId}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ textDecoration: 'none', color: 'inherit' }}
                        >
                          <Typography
                            variant="subtitle1"
                            sx={{
                              fontWeight: 600,
                              cursor: 'pointer',
                              '&:hover': { textDecoration: 'underline' }
                            }}
                          >
                            {player.playerName}
                          </Typography>
                        </Link>
                      )}
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <Chip
                          label={translateFromDB(player.position)}
                          size="small"
                          sx={{ fontSize: '0.7rem', height: 20 }}
                        />
                        {player.isNba && (
                          <Chip
                            label="NBA"
                            size="small"
                            color="warning"
                            sx={{ fontSize: '0.7rem', height: 20, fontWeight: 700 }}
                          />
                        )}
                      </Box>
                    </Box>
                  </Box>

                  {/* Similarity badge */}
                  <Box sx={{ textAlign: 'center', minWidth: 60 }}>
                    <Typography
                      variant="h5"
                      sx={{ fontWeight: 700, color: simColor, lineHeight: 1 }}
                    >
                      {player.similarity}<Typography component="span" variant="body2" sx={{ color: simColor }}>%</Typography>
                    </Typography>
                    <LinearProgress
                      variant="determinate"
                      value={player.similarity}
                      sx={{
                        mt: 0.5,
                        height: 4,
                        borderRadius: 2,
                        bgcolor: 'grey.200',
                        '& .MuiLinearProgress-bar': {
                          bgcolor: simColor,
                          borderRadius: 2
                        }
                      }}
                    />
                  </Box>
                </Box>

                <Divider sx={{ mb: 1 }} />

                {/* Metrics table */}
                <Table size="small">
                  <TableBody>
                    {/* Header row */}
                    <TableRow sx={{ '& td': { border: 0, py: 0.5 } }}>
                      <TableCell sx={{ fontWeight: 600, color: 'text.secondary', width: '30%' }} />
                      <TableCell
                        align="center"
                        sx={{ fontWeight: 700, color: 'text.secondary', fontSize: '0.75rem', textTransform: 'uppercase' }}
                      >
                        {data.targetPlayer?.name?.split(' ').pop() || t('measurements.similar.you')}
                      </TableCell>
                      <TableCell align="center" sx={{ width: 40 }} />
                      <TableCell
                        align="center"
                        sx={{ fontWeight: 700, color: 'text.secondary', fontSize: '0.75rem', textTransform: 'uppercase' }}
                      >
                        {player.playerName.split(' ').pop()}
                      </TableCell>
                      <TableCell align="right" sx={{ width: '20%' }} />
                    </TableRow>

                    {player.metrics.map((metric) => (
                      <TableRow
                        key={metric.key}
                        sx={{
                          '& td': { border: 0, py: 0.75 },
                          '&:hover': { bgcolor: 'action.hover' },
                          bgcolor: boostStats.includes(metric.key) ? 'primary.50' : 'inherit'
                        }}
                      >
                        <TableCell>
                          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: boostStats.includes(metric.key) ? 700 : 400 }}>
                            {metric.label}
                            {boostStats.includes(metric.key) && ' x2'}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2" sx={{ fontWeight: 600, color: 'error.main' }}>
                            {metric.targetValue} {metric.unit}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <CompareArrowsIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {metric.playerValue} {metric.unit}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 500, color: getDiffColor(metric.diff) }}
                          >
                            {formatDiff(metric.diff, metric.unit)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          );
        })}
      </Box>
    </Box>
  );
};

export default SimilarPlayersTab;
